import React, { useEffect, useState } from "react";
import { Invoice } from "../../api/invoiceproposal";

interface InvoiceDetailsModalProps {
    invoice: Invoice | null;
    isOpen: boolean;
    onClose: () => void;
}

const InvoiceDetailsModal: React.FC<InvoiceDetailsModalProps> = ({
    invoice,
    isOpen,
    onClose,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (isOpen) {
            setPosition({ x: 0, y: 0 });
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    const handleMouseDown = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest("button, a, .btn-close")) return;
        setIsDragging(true);
        setDragStart({
            x: e.clientX - position.x,
            y: e.clientY - position.y,
        });
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        setPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y,
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        } else {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        }
        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, dragStart]);

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleEscKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
            onClose();
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("keydown", handleEscKey);
        }
        return () => {
            document.removeEventListener("keydown", handleEscKey);
        };
    }, [isOpen]);

    if (!isOpen || !invoice) return null;

    return (
        <div
            className="modal fade show"
            style={{
                display: "block",
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.5)",
                zIndex: 1050,
                animation: "fadeIn 0.2s ease-in-out",
            }}
            onClick={handleBackdropClick}
        >
            <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .invoice-details-modal .modal-header {
          cursor: move;
          user-select: none;
          background: white;
          border-bottom: 1px solid #dee2e6;
          padding: 1rem 1.5rem;
        }
        .invoice-details-modal .modal-title {
          color: #dc3545;
          font-weight: 600;
        }
        .custom-btn-close {
          background: transparent;
          border: 1px solid #dc3545 !important;
          color: #dc3545 !important;
          font-size: 1.5rem;
          line-height: 1;
          opacity: 1;
          padding: 0;
          width: 32px;
          height: 32px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .custom-btn-close:hover {
          background: #dc3545 !important;
          color: white !important;
          opacity: 1;
        }
        .custom-btn-close i {
          font-size: 1.25rem;
        }
        .invoice-details-modal .modal-content {
          border: none;
          border-radius: 8px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          overflow: hidden;
          will-change: transform;
        }
        .invoice-details-modal .modal-dialog {
          transition: transform 0.05s ease-out;
        }
        .invoice-details-modal .modal-body {
          padding: 1.5rem;
        }
        .invoice-details-modal .modal-footer {
          border-top: 1px solid #e9ecef;
          padding: 1rem 1.5rem;
          background: #f8f9fa;
        }
        .info-section {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
        }
        .info-section h6 {
          color: #495057;
          font-weight: 600;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #667eea;
        }
        .info-row {
          display: flex;
          padding: 0.5rem 0;
          border-bottom: 1px solid #e9ecef;
        }
        .info-row:last-child {
          border-bottom: none;
        }
        .info-label {
          font-weight: 600;
          color: #6c757d;
          min-width: 200px;
          flex-shrink: 0;
        }
        .info-value {
          color: #212529;
          word-break: break-word;
        }
      `}</style>

            <div
                className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable invoice-details-modal"
                style={{
                    transform: `translate(${position.x}px, ${position.y}px)`,
                    pointerEvents: "auto",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header" onMouseDown={handleMouseDown}>
                        <h5 className="modal-title">
                            <i className="ti ti-eye me-2" />
                            Invoice Details
                        </h5>
                        <button
                            type="button"
                            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                            onClick={onClose}
                            aria-label="Close"
                        >
                            <i className="ti ti-x" />
                        </button>
                    </div>

                    <div className="modal-body">
                        {/* Invoice Information */}
                        <div className="info-section">
                            <h6>
                                <i className="ti ti-file-invoice me-2" />
                                Invoice Information
                            </h6>
                            <div className="info-row">
                                <span className="info-label">Invoice ID:</span>
                                <span className="info-value">
                                    {invoice.invoice_proposal_id || "N/A"}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Invoice Date:</span>
                                <span className="info-value">
                                    {invoice.invoice_date || "N/A"}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Validation Date:</span>
                                <span className="info-value">
                                    {invoice.validation_date || "N/A"}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Status:</span>
                                <span className="info-value">
                                    <span className={`badge bg-${invoice.status?.toLowerCase() === 'paid' ? 'success' : invoice.status?.toLowerCase() === 'cancelled' ? 'danger' : 'warning'}`}>
                                        {invoice.status || "N/A"}
                                    </span>
                                </span>
                            </div>
                            {invoice.amc && (
                                <div className="info-row">
                                    <span className="info-label">AMC:</span>
                                    <span className="info-value">{invoice.amc}</span>
                                </div>
                            )}
                        </div>

                        {/* Financial Details */}
                        <div className="info-section">
                            <h6>
                                <i className="ti ti-currency-dollar me-2" />
                                Financial Details
                            </h6>
                            <div className="info-row">
                                <span className="info-label">Total Amount:</span>
                                <span className="info-value">
                                    {invoice.deal?.currency || ""} {invoice.invoice_total_amount || "N/A"}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Subtotal:</span>
                                <span className="info-value">
                                    {invoice.deal?.currency || ""} {invoice.subtotalamount || "N/A"}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Tax Total:</span>
                                <span className="info-value">
                                    {invoice.deal?.currency || ""} {invoice.taxtotalamount || "N/A"}
                                </span>
                            </div>
                            {invoice.discount && (
                                <div className="info-row">
                                    <span className="info-label">Discount:</span>
                                    <span className="info-value">
                                        {invoice.discount} ({invoice.proposal_discount_percentage}%)
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Tax Breakdown */}
                        {(invoice.cgst || invoice.sgst || invoice.igst) && (
                            <div className="info-section">
                                <h6>
                                    <i className="ti ti-receipt-tax me-2" />
                                    Tax Breakdown
                                </h6>
                                {invoice.cgst && (
                                    <div className="info-row">
                                        <span className="info-label">CGST ({invoice.cgst_percentage}%):</span>
                                        <span className="info-value">{invoice.cgst}</span>
                                    </div>
                                )}
                                {invoice.sgst && (
                                    <div className="info-row">
                                        <span className="info-label">SGST ({invoice.sgst_percentage}%):</span>
                                        <span className="info-value">{invoice.sgst}</span>
                                    </div>
                                )}
                                {invoice.igst && (
                                    <div className="info-row">
                                        <span className="info-label">IGST ({invoice.igst_percentage}%):</span>
                                        <span className="info-value">{invoice.igst}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Product & Deal */}
                        {invoice.deal && (
                            <div className="info-section">
                                <h6>
                                    <i className="ti ti-box me-2" />
                                    Product & Deal
                                </h6>
                                <div className="info-row">
                                    <span className="info-label">Product Name:</span>
                                    <span className="info-value">
                                        {invoice.deal.product?.product_name || "N/A"}
                                    </span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Product Price:</span>
                                    <span className="info-value">
                                        {invoice.deal.currency} {invoice.deal.product?.product_price || "N/A"}
                                    </span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Deal Value:</span>
                                    <span className="info-value">
                                        {invoice.deal.currency} {invoice.deal.deal_value || "N/A"}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Contact Information */}
                        {invoice.deal?.contact && (
                            <div className="info-section">
                                <h6>
                                    <i className="ti ti-user me-2" />
                                    Contact Information
                                </h6>
                                <div className="info-row">
                                    <span className="info-label">Company Name:</span>
                                    <span className="info-value">
                                        {invoice.deal.contact.company_name || "N/A"}
                                    </span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Contact Person:</span>
                                    <span className="info-value">
                                        {invoice.deal.contact.contact_person_name || "N/A"}
                                    </span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Email:</span>
                                    <span className="info-value">
                                        {invoice.deal.contact.company_mail || "N/A"}
                                    </span>
                                </div>
                                {invoice.deal.contact.company_phone && (
                                    <div className="info-row">
                                        <span className="info-label">Phone:</span>
                                        <span className="info-value">
                                            {invoice.deal.contact.company_phone}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Notes & Terms */}
                        {(invoice.notes || invoice.termsandconditions) && (
                            <div className="info-section">
                                <h6>
                                    <i className="ti ti-notes me-2" />
                                    Notes & Terms
                                </h6>
                                {invoice.notes && (
                                    <div className="info-row">
                                        <span className="info-label">Notes:</span>
                                        <span className="info-value">{invoice.notes}</span>
                                    </div>
                                )}
                                {invoice.termsandconditions && (
                                    <div className="info-row">
                                        <span className="info-label">Terms & Conditions:</span>
                                        <span className="info-value">{invoice.termsandconditions}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Created/Updated */}
                        {(invoice.created_at || invoice.updated_at) && (
                            <div className="text-center text-muted small mt-3">
                                {invoice.created_at && (
                                    <div>
                                        <i className="ti ti-calendar me-1" />
                                        Created: {new Date(invoice.created_at).toLocaleString()}
                                    </div>
                                )}
                                {invoice.updated_at && (
                                    <div className="mt-1">
                                        <i className="ti ti-clock me-1" />
                                        Updated: {new Date(invoice.updated_at).toLocaleString()}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-light px-4"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceDetailsModal;
